import React, { useEffect, useState, useRef } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

export default function CustomerServiceModal({ show, onHide }) {
  const [messages, setMessages] = useState([]);
  const [admin, setAdmin] = useState(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const { socket, user, unreadCount, setUnreadCount } = useAuth();

  // 拉取历史消息
  const fetchMessages = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/messages/admin');
      console.log('客服历史消息', res.data);
      setMessages(res.data.data || []);
      setAdmin(res.data.admin || null);
    } catch (e) {
      // 可根据需要提示
    } finally {
      setLoading(false);
    }
  };

  // 发送消息
  const sendMessage = async () => {
    if (!input.trim() || !admin) return;
    setSending(true);
    try {
      await api.post('/api/messages', { to: admin._id, content: input.trim() });
      setInput('');
      // 不再立即 fetchMessages，等待 socket 推送
    } catch (e) {
      alert(e.response?.data?.message || '发送失败');
    } finally {
      setSending(false);
    }
  };

  // 拉取历史消息后再监听 socket
  useEffect(() => {
    if (!show) return;
    fetchMessages();
  }, [show]);

  // 监听 socket 消息，依赖 admin 和 user
  useEffect(() => {
    if (!socket || !admin || !user) return;
    const handler = (msg) => {
      // 只要和当前会话相关（from/to 有一个等于 admin._id，另一个等于 user._id）就追加
      if (
        (String(msg.from) === String(admin._id) && String(msg.to) === String(user._id)) ||
        (String(msg.to) === String(admin._id) && String(msg.from) === String(user._id))
      ) {
        setMessages(prev => {
          // 防止重复追加
          if (prev.some(m => m._id === msg._id)) return prev;
          return [...prev, msg];
        });
      }
    };
    socket.on('newMessage', handler);
    return () => socket.off('newMessage', handler);
  }, [socket, admin, user]);

  // 自动滚动到底部
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (show) {
      setUnreadCount(0);
      window.customerServiceModalOpen = true;
      // 批量标记未读为已读
      messages.forEach(msg => {
        if (!msg.isRead && msg.to === user._id) {
          api.post(`/api/messages/${msg._id}/read`).catch(() => {});
        }
      });
    } else {
      window.customerServiceModalOpen = false;
    }
  }, [show, messages, user, setUnreadCount]);

  return (
    <Modal show={show} onHide={onHide} centered size="md" dialogClassName="glass-modal">
      <Modal.Header closeButton>
        <Modal.Title>联系客服</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ minHeight: 350, maxHeight: 500, overflowY: 'auto', background: '#181c24' }}>
        {loading ? (
          <div className="text-center"><Spinner animation="border" size="sm" /> 加载中…</div>
        ) : (
          <div style={{ maxHeight: 400, overflowY: 'auto', padding: 4 }}>
            {(!admin || messages.length === 0) ? (
              <div className="text-muted text-center">暂无消息，欢迎咨询管理员！</div>
            ) : (
              messages.map(msg => (
                <div key={msg._id} style={{
                  textAlign: msg.from === admin._id ? 'left' : 'right',
                  margin: '8px 0'
                }}>
                  <div style={{
                    display: 'inline-block',
                    background: msg.from === admin._id ? '#222' : '#0ff',
                    color: msg.from === admin._id ? '#fff' : '#222',
                    borderRadius: 8,
                    padding: '6px 12px',
                    maxWidth: 260,
                    wordBreak: 'break-all',
                  }}>
                    {msg.content}
                  </div>
                  <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
                    {new Date(msg.createdAt).toLocaleString()}
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer style={{ background: '#181c24' }}>
        <Form.Control
          type="text"
          placeholder="请输入消息..."
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') sendMessage(); }}
          disabled={sending || !admin}
          style={{ width: '70%' }}
        />
        <Button variant="primary" onClick={sendMessage} disabled={sending || !input.trim() || !admin} style={{ marginLeft: 8 }}>
          {sending ? <Spinner animation="border" size="sm" /> : '发送'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
